import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography, CircularProgress, IconButton, TextField, Button, InputAdornment } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { Client as ConversationsClient, Conversation } from '@twilio/conversations';
import SendIcon from '@mui/icons-material/Send';
import EmojiEmotionsIcon from '@mui/icons-material/EmojiEmotions';
import MessageList from './MessageList';
import type { Message, Participant, User } from '@twilio/conversations';
import { initClientListeners, initConversationListener, initMessagesListener, initParticipantsListener, resetRestartButtonShown } from '../../twilio/store/listeners';
import { ACTION_ADD_MULTIPLE_MESSAGES, ACTION_SET_MESSAGES, ACTION_START_SESSION, ACTION_UPDATE_CONNECTION_STATE } from '../../twilio/store/actionTypes';
import { RootState } from '../../redux/store';
import { sessionDataHandler } from '../../sessionDataHandler';
import { setTwilioClient, setTwilioConversation, setTwilioMessages, setTwilioParticipants, setTwilioUsers } from '../../redux/actions/chatActions';
import AttachFileButton, { AttachFileButtonRef } from './AttachFileButton';
import FilePreview from './FilePreview';
import { detachFiles } from '../../twilio/store/genericActions';
import EmojiPicker from 'emoji-picker-react';
import zIndex from '@mui/material/styles/zIndex';
import { executeBBCard } from '../../utils/bbCardService';
import { signInTwilio } from '../../redux/slices/userSlice';
import { useAppDispatch } from '../../redux/store';
import { changeEngagementPhase } from '../../twilio/store/genericActions';
import { EngagementPhase } from '../../twilio/store/definitions';
import { setPage } from '../../redux/slices/pageSlice';
import { initSession } from '../../twilio/store/initSession';
import { externalEventService } from '../../services/externalEventService';
import store from '../../redux/store';
import KeyboardControlKeyIcon from '@mui/icons-material/KeyboardControlKey';
import { useIpAndCountry } from '../../hooks/useIpAndCountry';

// lightweight throttle to avoid external dependency
function throttle<T extends (...args: any[]) => void>(fn: T, wait: number) {
  let last = 0;
  let timeout: ReturnType<typeof setTimeout> | null = null;
  let lastArgs: any[] | null = null;

  const invoke = () => {
    last = Date.now();
    timeout = null;
    fn.apply(null, lastArgs as any[]);
    lastArgs = null;
  };

  return (...args: any[]) => {
    lastArgs = args;
    const now = Date.now();
    const remaining = wait - (now - last);

    if (remaining <= 0 || remaining > wait) {
      if (timeout) {
        clearTimeout(timeout);
        timeout = null;
      }
      invoke();
    } else if (!timeout) {
      timeout = setTimeout(invoke, remaining);
    }
  };
}

// Wait for Conversations client to initialize before using it
async function waitForClientInitialization(client: ConversationsClient, timeoutMs = 10000): Promise<void> {
  // @ts-ignore access internal state safely
  const current = (client as any)?.state || (client as any)?._state;
  if (current === 'initialized' || current === 'connected') return;
  return new Promise<void>((resolve, reject) => {
    const timer = setTimeout(() => {
      // eslint-disable-next-line no-use-before-define
      client.removeListener?.('stateChanged', onStateChanged as any);
      reject(new Error('Twilio Conversations client init timeout'));
    }, timeoutMs);
    const onStateChanged = (state: string) => {
      if (state === 'initialized' || state === 'connected') {
        clearTimeout(timer);
        client.removeListener?.('stateChanged', onStateChanged as any);
        resolve();
      }
    };
    client.addListener?.('stateChanged', onStateChanged as any);
  });
}

const PAGE_SIZE = 30;
// Wait for client connection ready
async function waitForConnected(client: ConversationsClient, timeoutMs = 10000): Promise<void> {
  const current = (client as any)?.connectionState;
  if (current === 'connected') return;
  return new Promise<void>((resolve, reject) => {
    const timer = setTimeout(() => {
      client.removeListener?.('connectionStateChanged', onChanged as any);
      reject(new Error('Twilio Conversations client connect timeout'));
    }, timeoutMs);
    const onChanged = (state: string) => {
      if (state === 'connected') {
        clearTimeout(timer);
        client.removeListener?.('connectionStateChanged', onChanged as any);
        resolve();
      }
    };
    client.addListener?.('connectionStateChanged', onChanged as any);
  });
}

async function getConversationWithRetry(client: ConversationsClient, sid: string, retries = 3): Promise<Conversation> {
  let lastErr: any;
  for (let i = 0; i < retries; i++) {
    try {
      const conv = await client.getConversationBySid(sid);
      return conv;
    } catch (e) {
      lastErr = e;
      await new Promise((r) => setTimeout(r, 500 * (i + 1)));
    }
  }
  throw lastErr;
}


const containerStyles = {
  position: 'relative',
  height: {
    xs: 'calc(100dvh - 60px)', 
    sm: 'calc(100dvh - 140px)', 
  },
  display: 'flex',
  flexDirection: 'column',
};

const inputBarStyles = {
  position: "absolute" as const,
  bottom: {
    xs: 0,
    sm: -60,
  },
  left: 0,
  right: 0,
  padding: "10px",
  display: "flex",
  gap: "8px",
  width: "100%",
};

const previewBarStyles = {
  position: 'absolute' as const,
  left: 0,
  right: 0,
    bottom: {
    xs: 65,
    sm: 0,
  },
  display: 'flex',
  gap: '8px',
  flexWrap: 'wrap' as const,
  padding: '8px 10px',
};

const sendButtonStyle = {
    position: 'absolute' as const,
    zIndex: 2,
    right: '24px',
    top: '18px',
   
}
const emojiButtonStyles = {
  position: 'absolute' as const,
  zIndex: 2,
  left: '19px',
  top: '18px',
}

const REGION_MISMATCH_MESSAGE =
  "You've accessed a non-India support page. We have rerouted you now to India chat support.";

  const getBrandSegmentFromUrl = (): string | null => {
  const path = window.location.pathname.toLowerCase();
  const match = path.match(/\/chat\/[^/]+\/([^/?#]+)/);
  return match ? match[1] : null;
};

const getSiteRegionFromUrl = (): 'PK' | 'EN' | 'BN' | null => {
  const path = window.location.pathname.toLowerCase();
  const match = path.match(/\/chat\/(pk|en|bn)(?:\/|$)/);
  return match ? (match[1].toUpperCase() as 'PK' | 'EN' | 'BN') : null;
};

const statusBar = (bg: string, text: string) => (
  <Typography sx={{ background: bg, color: '#fff', display: 'flex', alignItems: 'center', padding: '8px' }} mt={0.5}>
    {text}
  </Typography>
);
interface SignInTwilioResponse {
  userData: {
    token: string;
    conversationSid: string;
    [key: string]: any;
  };
}
interface TwilioChatProps {
  mParam?: string | null;
  onRestart?: (result: SignInTwilioResponse) => void;
}

const sleep = (ms: number) =>
  new Promise<void>(resolve => setTimeout(resolve, ms));

let hasVirtualAgent = false;

// Helper function to extract first name from email or encoded email (same as in listeners.ts)
const extractFirstName = (fullName: string): string => {
  if (!fullName || fullName === 'Unknown User') {
    return fullName;
  }

  // Handle encoded email format (e.g., "gita_2Egustianingsih_40sportserve_2Eco")
  if (fullName.includes('_2E') || fullName.includes('_40')) {
    // Decode the email: _2E = . and _40 = @
    const decodedEmail = fullName.replace(/_2E/g, '.').replace(/_40/g, '@');
    // Extract first name from decoded email
    const firstPart = decodedEmail.split('@')[0];
    return firstPart.split('.')[0];
  }

  // Handle regular email format (e.g., "gita.Egustianingsih@sportserve.co")
  if (fullName.includes('@')) {
    const firstPart = fullName.split('@')[0];
    return firstPart.split('.')[0];
  }

  // If it's already just a name, return as is
  return fullName;
};

// Helper function to check if any agent (real or virtual) is connected
const isAgentConnected = (parts: any[], users: any[] = []) => {
  // Check for real human agents (with email patterns)
  const hasRealAgent = parts.some(p => /_2E|_40/.test(p.identity));
  if (hasRealAgent) return true;

  // Check for virtual agents (participants that resolve to 'Unknown User')
  hasVirtualAgent = parts.some(p => {
    // Skip customer participants (those without email patterns are likely customers)
    if (/_2E|_40/.test(p.identity)) return false;

    // Find corresponding user
    const correspondingUser = users.find(u => u.identity === p.identity);
    const rawName = correspondingUser?.friendlyName || p.identity || 'Unknown User';
    const participantName = extractFirstName(rawName);

    // If it resolves to 'Unknown User', it's a virtual agent
    return participantName === 'Unknown User';
  });

  return hasVirtualAgent;
};

const getAgentName = (parts: any[], users: any[] = []) => {
  const agentParticipant = parts.find(p => /_2E|_40/.test(p.identity));
  if (agentParticipant) {
    // Find the corresponding user for this participant to get friendlyName
    const correspondingUser = users.find(u => u.identity === agentParticipant.identity);

    // Prioritize friendlyName over identity, same as createParticipantEventMessage
    const rawName = correspondingUser?.friendlyName || agentParticipant.identity || 'Unknown User';
    let participantName = extractFirstName(rawName);

    // If extraction results in 'Unknown User', default to 'agent'
    if (participantName === 'Unknown User') {
      participantName = 'agent';
    }

    return participantName;
  }
  return 'agent';
};

// DafaPlus only: mirror chat messages through localStorage so every tab of the same
// browser sitting on the same conversation shows the same lines live.
const getDafaplusMessagesStorageKey = (conversationSid: string) => `dafaplusChatMessages:${conversationSid}`;

const deserializeStoredMessage = (stored: any) => ({
  ...stored,
  dateCreated: stored.dateCreated ? new Date(stored.dateCreated) : undefined,
});

export default function TwilioChat({ mParam }: TwilioChatProps = {}) {
  const dispatch = useDispatch();
  const dispatchTwilio = useAppDispatch();
  const { token, conversationSid, engagementParams, userStatus } = useSelector((state: RootState) => ({
    token: (state.user as any)?.user?.token,
    conversationSid: (state.user as any)?.user?.conversationSid,
    engagementParams: (state.user as any)?.user?.engagementParameters,
    userStatus: (state.user as any)?.status,
  }));

  const { messages = [], participants = [], users = [], conversation, conversationState, connectionState, attachedFiles = [] } = useSelector(
    (state: RootState) => ({
      messages: (state as any)?.twilioChat?.messages || [],
      participants: (state as any)?.twilioChat?.participants || [],
      users: (state as any)?.twilioChat?.users || [],
      conversation: (state as any)?.twilioChat?.conversation,
      conversationState: (state as any)?.twilioChat?.conversationState,
      connectionState: (state as any)?.twilioChat?.connectionState,
      attachedFiles: (state as any)?.twilioChat?.attachedFiles || [],
    })
  );


const configState = useSelector((state: RootState) => state.config); // the whole slice
const config = configState.config; // 👈 the actual Strapi response
const formData = config?.data?.[0]?.attributes;

  const [isInitializing, setIsInitializing] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [isRestarting, setIsRestarting] = useState(false);
  const [text, setText] = useState('');
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [isEmojiShown, setIsEmojiShown] = useState(false);
  const [disabledRichMediaSids, setDisabledRichMediaSids] = useState<Set<string>>(new Set());

  const [dafaplusBrand, setdafaplusBrand] = useState(false);
  const { countryCode: liveCountryCode } = useIpAndCountry();
  const [spinmileBrand, setspinmileBrand] = useState(false);
  // Tracks each rich media button click as { key: action.payload, value: action.text },
  // so the raw payload sent as the message body can be swapped back for its display text in chat history.
  const [richMediaClickHistory, setRichMediaClickHistory] = useState<{ key: string; value: string }[]>([]);

const urlBrandSegment = useMemo(() => getBrandSegmentFromUrl(), []);
const templateBrand = (formData?.Template?.Brand || '').toLowerCase();
const isDafabetBrand = templateBrand === 'dafabet' || urlBrandSegment === 'dafabet';

const siteRegion = useMemo(() => {
  return getSiteRegionFromUrl();
}, []);

const detectedUserCountry = (liveCountryCode || engagementParams?.country || '').toUpperCase();
const isIndiaAccessingPkBd =
  isDafabetBrand &&
  detectedUserCountry === 'IN' &&
  (siteRegion === 'PK' || siteRegion === 'EN' || siteRegion === 'BN');

  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const loadingOlderRef = useRef(false);
  const conversationRef = useRef<Conversation | null>(null);
  const prevAttachedCountRef = useRef(0);
  const isSubmitDisabled = useMemo(() => isSending || (!text.trim() && attachedFiles.length === 0) || (!conversationRef.current && !token), [isSending, text, attachedFiles, token]);

  const INACTIVITY_TIMEOUT = 1 * 90 * 1000; // 90 seconds
  const inactivityCountRef = useRef(0);
  const inactivityTimerRef = useRef<number | null>(null);
  const hasSentIdleMessageRef = useRef(false);

  // Waiting for Agent API polling
  const waitingForAgentIntervalRef = useRef<number | null>(null);
  const waitingForAgentInitialDelayRef = useRef<number | null>(null);
  const lastMessageCountRef = useRef<number>(0);
  const waitingMessageStepRef = useRef<number>(0);

  const twilioApiBaseUrl = (
    process.env.REACT_APP_TWILIO_API_URL ||
    'https://istg-webrtc-chatcon.elysium-dfbt.com/connector/v3'
  )
    .trim()
    .replace(/\/$/, '');

  const onHoldMessage = formData?.ChatBox?.OnHoldMessage || 'We are waiting for agent';
  const onHoldMessage1 = formData?.ChatBox?.OnHoldMessage1 || 'We are waiting for agent';
  const onHoldMessage2 = formData?.ChatBox?.OnHoldMessage2 || 'We are waiting for agent';
  const parsedOnHoldMessageTime = Number(formData?.ChatBox?.OnHoldMessageTime);
  const onHoldMessageTime = Number.isFinite(parsedOnHoldMessageTime) && parsedOnHoldMessageTime > 0
    ? parsedOnHoldMessageTime
    : 60000;

  const getOnHoldMessageByStep = useCallback((step: number) => {
    if (step === 0) return onHoldMessage;
    if (step === 1) return onHoldMessage1;
    return onHoldMessage2;
  }, [onHoldMessage, onHoldMessage1, onHoldMessage2]);
  
  // Function to send "We are waiting for agent" message via API
  const sendWaitingForAgentMessage = useCallback(async (messageBody: string) => {
    try {
      const response = await fetch(`${twilioApiBaseUrl}/sendUserMessage`, {
        method: 'POST',
        headers: {
          'origin': 'https://istg-chat.elysium-dfbt.com',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          conversationSid: conversationSid,
          messageBody,
          author: 'System'
        })
      });
      
      if (response.ok) {
        console.log('✅ Waiting for agent message sent successfully');
      } else {
        console.error('❌ Failed to send waiting for agent message:', response.status);
      }
    } catch (error) {
      console.error('❌ Error sending waiting for agent message:', error);
    }
  }, [conversationSid, twilioApiBaseUrl]);

  // Manage waiting for agent polling
  useEffect(() => {
    const convState = (conversationState as any) || 'active';
    const shouldWait = convState === 'active' && !isAgentConnected(participants, users);

    if (shouldWait) {
      console.log("this is test")
      // Set initial delay
      waitingForAgentInitialDelayRef.current = window.setTimeout(() => {
        console.log('🕐 No agent detected - starting polling');
        const firstMessage = getOnHoldMessageByStep(waitingMessageStepRef.current);
        sendWaitingForAgentMessage(firstMessage);
        waitingMessageStepRef.current = Math.min(waitingMessageStepRef.current + 1, 2);
        
        // After initial delay, poll on configured interval
        waitingForAgentIntervalRef.current = window.setInterval(() => {
          const currentMessageCount = messages.length;
          
          if (currentMessageCount === lastMessageCountRef.current) {
            console.log('📤 Polling: No new messages, sending waiting message...');
            const nextMessage = getOnHoldMessageByStep(waitingMessageStepRef.current);
            sendWaitingForAgentMessage(nextMessage);
            waitingMessageStepRef.current = Math.min(waitingMessageStepRef.current + 1, 2);
          } else {
            console.log('📨 Polling: New messages received, resetting timer');
            lastMessageCountRef.current = currentMessageCount;
          }
        }, onHoldMessageTime);
      }, onHoldMessageTime);

      lastMessageCountRef.current = messages.length;
    } else {
      // Clear timers if conditions are not met
      if (waitingForAgentInitialDelayRef.current !== null) {
        clearTimeout(waitingForAgentInitialDelayRef.current);
        waitingForAgentInitialDelayRef.current = null;
      }
      if (waitingForAgentIntervalRef.current !== null) {
        clearInterval(waitingForAgentIntervalRef.current);
        waitingForAgentIntervalRef.current = null;
      }
      waitingMessageStepRef.current = 0;
    }

    // Cleanup on unmount or when dependencies change
    return () => {
      if (waitingForAgentInitialDelayRef.current !== null) {
        clearTimeout(waitingForAgentInitialDelayRef.current);
      }
      if (waitingForAgentIntervalRef.current !== null) {
        clearInterval(waitingForAgentIntervalRef.current);
      }
    };
  }, [conversationState, participants, users, messages, sendWaitingForAgentMessage, onHoldMessageTime, getOnHoldMessageByStep]);

  let isWaiting = false;
  const convState = (conversationState as any) || 'active';
  const isConnectionRetrying = connectionState === 'retrying';
  const attachFileRef = useRef<AttachFileButtonRef>(null);
  // If Redux already has a conversation object, mirror it into the ref so UI becomes enabled
  useEffect(() => {
    console.log('🔄 Conversation updated in Redux:', {
      hasConversationRef: !!conversationRef.current,
      hasConversation: !!conversation,
      conversationSid: (conversation as any)?.sid,
      hasToken: !!token,
      hasConversationSid: !!conversationSid
    });

    // Always update conversationRef when conversation changes (including after restart)
    if (conversation) {
      conversationRef.current = conversation as Conversation;
      console.log('✅ ConversationRef updated with new conversation');
    } else if (!conversation && !token && !conversationSid) {
      // Only clear conversationRef if we're truly disconnected (no token/conversationSid)
      conversationRef.current = null;
      console.log('🔄 ConversationRef cleared - no active session');
    }
  }, [conversation, token, conversationSid]);

  // Initialize Twilio client and conversation -> wire Redux listeners
  useEffect(() => {
    let mounted = true;
    const init = async () => {
      if (!token || !conversationSid) return;
      try {
        const client = await ConversationsClient.create(token);
        if (!mounted) return;
        await waitForClientInitialization(client);
        await waitForConnected(client);
        if (!mounted) return;
        // Ensure subscriptions are ready (prevents internal myConversationsRead errors in some SDK versions)
        try { await client.getSubscribedConversations(); } catch {}
        const conversationInstance = await getConversationWithRetry(client, conversationSid, 5);
        if (!mounted) return;
        conversationRef.current = conversationInstance;

        // Initial data
        const count = await conversationInstance.getMessagesCount();
        setTotalCount(count ?? 0);
        const initialPaginator = await conversationInstance.getMessages(Math.min(PAGE_SIZE, count ?? PAGE_SIZE));
        const initialMessages = initialPaginator.items;
        const parts = await conversationInstance.getParticipants();
        const users = await Promise.all(parts.map(async (p) => {
          try {
            // Only chat participants support getUser
            if ((p as any)?.type === 'chat') {
              return await (p as any).getUser();
            }
          } catch {}
          return { identity: (p as any)?.identity } as any;
        }));

        // Dispatch start session (do not store client in Redux)
        console.log('🎯 TWILIO CHAT SESSION STARTING:', {
          conversationSid: conversationSid,
          participantCount: parts.length,
          messageCount: initialMessages.length,
          totalMessageCount: count,
          conversationState: (conversationInstance as any)?.state || 'active'
        });

        dispatch({
          type: ACTION_START_SESSION,
          payload: {
            conversation: conversationInstance,
            conversationState: (conversationInstance as any)?.state || 'active',
            users,
            participants: parts,
            messages: initialMessages,
          },
        });

        // Mirror initial data for legacy selectors
        dispatch(setTwilioMessages(initialMessages));
        dispatch(setTwilioParticipants(parts));
        dispatch(setTwilioUsers(users));

        // Listeners
        const baseUrl = (process.env.REACT_APP_TWILIO_API_URL || process.env.REACT_APP_CHATCON_URL || 'http://localhost:3010').trim();
        sessionDataHandler.setEndpoint(baseUrl);
        const getUpdatedToken = async () => {
          try {
            const updated = await sessionDataHandler.getUpdatedToken();
            return { token: updated.token, conversationSid: updated.conversationSid };
          } catch {
            return null;
          }
        };

        // Mirror into legacy chat slice for components that still read from state.chat
        dispatch(setTwilioClient(client));
        dispatch(setTwilioConversation(conversationInstance));

        console.log('🔗 INITIALIZING TWILIO CHAT LISTENERS');
        initClientListeners(client, dispatch, getUpdatedToken, conversationInstance);
        initConversationListener(conversationInstance, dispatch);
        await initMessagesListener(conversationInstance, dispatch);
        initParticipantsListener(conversationInstance, dispatch);

        console.log('✅ TWILIO CHAT INITIALIZATION COMPLETED SUCCESSFULLY');
        setIsInitializing(false);
        setIsRestarting(false); // Reset restart button state when initialization is complete
      } catch (e) {
        console.error('❌ FAILED TO INITIALIZE TWILIO CHAT:', e);
        setIsInitializing(false);
        setIsRestarting(false); // Reset restart button state even if initialization fails
      }
    };
    init();
    return () => {
      mounted = false;
    };
  }, [token, conversationSid, dispatch, engagementParams]);

  const scrollToBottom = useCallback(() => {
    if (!listRef.current) return;
    listRef.current.scrollTop = listRef.current.scrollHeight;
  }, []);

useEffect(() => {
  const container = listRef.current;
  if (!container) return;

  container.scrollTo({
    top: container.scrollHeight,
    behavior: 'smooth', // or 'auto'
  });
}, [messages]);

useEffect(() => {
  if (convState !== "active") {
    window.scrollTo({
      top: document.body.scrollHeight,
      behavior: "smooth",
    });
  }
}, [convState]);

  useEffect(() => {
    if (attachedFiles.length > prevAttachedCountRef.current) {
      if (!text.trim()) setText('File Uploaded');
      scrollToBottom();
    }
    prevAttachedCountRef.current = attachedFiles.length;
  }, [attachedFiles, text, scrollToBottom]);

  useEffect(() => {
    if (Object.keys(engagementParams || {}).length > 0) {
      console.log("bbc send",engagementParams)
      // executeBBCard(engagementParams);
    }
  }, [engagementParams]);

  const handleScroll = async (e: React.UIEvent<HTMLDivElement>) => {
    const conv = conversationRef.current;
    if (!conv || loadingOlderRef.current) return;
    const el = e.currentTarget;
    const atTop = el.scrollTop <= 0;
    if (atTop && totalCount !== null && messages.length < totalCount) {
      loadingOlderRef.current = true;
      try {
        const anchor = Math.max(0, (totalCount as number) - messages.length - 1);
        const toLoad = Math.min(PAGE_SIZE, (totalCount as number) - messages.length);
        const paginator = await conv.getMessages(toLoad, anchor);
        dispatch({ type: ACTION_ADD_MULTIPLE_MESSAGES, payload: { messages: paginator.items } });
      } finally {
        loadingOlderRef.current = false;
      }
    }
  };

  const throttledTyping = useMemo(() => throttle(() => conversationRef.current?.typing(), 500), []);

  const resetInactivityTimer = useCallback(() => {
    if (inactivityTimerRef.current !== null) {
      window.clearTimeout(inactivityTimerRef.current);
    }
  
    hasSentIdleMessageRef.current = false;
  
    inactivityTimerRef.current = window.setTimeout(() => {
      const sendIdleMessage = async () => {
        if (inactivityCountRef.current >= 2) return;
  
        const conv = conversationRef.current;
        if (!conv || hasSentIdleMessageRef.current) return;
  
        try {
          await conv.sendMessage("");
          hasSentIdleMessageRef.current = true;
          inactivityCountRef.current += 1;
  
          console.log("⏳ Inactivity message sent");
  
          if (inactivityCountRef.current < 2) {
            resetInactivityTimer();
          }
        } catch (error) {
          console.error("Failed to send inactivity message", error);
        }
      };
  
      if (hasVirtualAgent) {
        void sendIdleMessage();
      }
    }, INACTIVITY_TIMEOUT);
  }, []);
    
useEffect(() => {

  // Start timer initially
  resetInactivityTimer();

  return () => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }
  };
}, [resetInactivityTimer]);

  

const send = async () => {
  setIsEmojiShown(false);
  const conv = conversationRef.current;

  if (!conv || (!text.trim() && attachedFiles.length === 0) || isSending) return;

  setIsSending(true);

  try {

      setDisabledRichMediaSids(prev => {
      const next = new Set(prev);

      messages.forEach((m: { sid: string; }) => {
        if ((m as any)._richMediaMetadata) {
          next.add(m.sid); // or m.index if sid not available
        }
      });

      return next;
    });

    if (attachedFiles.length > 0) {
      let preparedMsg = conv.prepareMessage();

      if (text.trim()) {
        preparedMsg = preparedMsg.setBody(text.trim());
      }

      for (const file of attachedFiles) {
        const formData = new FormData();
        formData.append('file', file, file.name);
        preparedMsg = preparedMsg.addMedia(formData as any);
      }

      const msg = await preparedMsg.build();
      await msg.send();

      attachFileRef.current?.showSuccess(
        formData?.ChatBox?.FileUploadSuccessMessage ?? 'File uploaded successfully.'
      );

      try {
        dispatch(detachFiles({ filesToDetach: attachedFiles }));
      } catch (e) {
        console.error('Error clearing attached files', e);
      }
    } else if (text.trim()) {
      await conv.sendMessage(text.trim());
    }

    setText('');
    await conv.setAllMessagesRead();
  } catch (e) {
    console.error('Failed to send message', e);
  } finally {
    setIsSending(false);
    setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
    await sleep(6000);
    //Inactivity timer
    resetInactivityTimer();
  }
};


  // Enhanced rich media detection with detailed analysis
  const checkMessagesForRichMedia = async (messages: any[]) => {
    console.log('🔍 Enhanced Rich Media Detection Starting...');
    const richMediaMessages: any[] = [];

    for (let i = 0; i < messages.length; i++) {
      const message = messages[i];
      console.log(`📊 Message ${i} Analysis:`, {
        hasAttachedMedia: !!message.attachedMedia && message.attachedMedia.size > 0,
        attachedMediaCount: message.attachedMedia ? message.attachedMedia.size : 0,
        hasAttributes: !!message.attributes && Object.keys(message.attributes).length > 0,
        attributes: message.attributes,
        messageType: message.type || 'text',
        hasMedia: !!message.media,
        media: message.media,
        contentSid: message.contentSid,
        hasContentSid: !!message.contentSid,
        participantSid: message.participantSid,
        hasSubject: !!message.subject,
        subject: message.subject
      });

      // Check for various rich media indicators
      const richMediaIndicators: string[] = [];

      if (message.attachedMedia && message.attachedMedia.size > 0) {
        richMediaIndicators.push('has attached media files');
      }

      if (message.attributes && Object.keys(message.attributes).length > 0) {
        richMediaIndicators.push('has message attributes');
      }

      if (message.contentSid) {
        richMediaIndicators.push('has contentSid');
      }

      if (message.media) {
        richMediaIndicators.push('has media property');
      }

      if (message.type && message.type !== 'text') {
        richMediaIndicators.push(`non-text message type: ${message.type}`);
      }

      if (message.subject) {
        richMediaIndicators.push('has subject');
      }

      if (richMediaIndicators.length > 0) {
        console.log(`🎨 Message ${i} has rich media indicators: ${richMediaIndicators.join(', ')}`);
        richMediaMessages.push(message);
      }

      // Try to get channel metadata with enhanced error handling
      if (typeof message.getChannelMetadata === 'function') {
        try {
          const metadata = await message.getChannelMetadata();
          if (metadata) {
            console.log(`📋 Message ${i} Channel Metadata:`, metadata);

            // Check for rich media in metadata
            if (metadata.data?.queryResult?.responseMessages) {
              const responseMessages = metadata.data.queryResult.responseMessages;
              console.log(`🎯 Message ${i} has ${responseMessages.length} response messages in metadata`);

              // Check each response message for rich content
              responseMessages.forEach((response: any, idx: number) => {
                if (response.payload) {
                  console.log(`📦 Response ${idx} has payload:`, response.payload);

                  // Check for actions in payload - this is the key rich media indicator!
                  if (response.payload.payload?.actions && response.payload.payload.actions.length > 0) {
                    console.log(`🔘 Response ${idx} has ${response.payload.payload.actions.length} interactive actions:`,
                      response.payload.payload.actions);

                    // Mark this message as having rich media with interactive buttons
                    if (!richMediaMessages.includes(message)) {
                      richMediaMessages.push(message);
                      console.log(`✨ Message ${i} added to rich media list due to interactive actions`);
                    }

                    // Store the metadata on the message for rendering
                    (message as any)._richMediaMetadata = metadata;
                  }
                }
              });
            }
          } else {
            console.log(`📝 Message ${i}: Channel metadata is null`);
          }
        } catch (error) {
          console.log(`📝 Message ${i}: No channel metadata available (fetch failed)`);
        }
      } else {
        console.log(`📝 Message ${i}: getChannelMetadata method not available`);
      }
    }

    console.log(`✅ Rich Media Detection Complete: Found ${richMediaMessages.length} messages with rich media indicators`);
    return richMediaMessages;
  };

  // Handle rich media action clicks
  const handleRichMediaAction = async (action: any) => {
    console.log('🔘 Rich Media Action Clicked:', action);

    const conv = conversationRef.current;
    if (!conv) {
      console.error('❌ No conversation available for sending action');
      return;
    }

    try {
      // Send the action payload as a message
      if (action.payload) {
        // Record the payload -> display text mapping so chat history can show
        // the button's label instead of the raw payload once this message arrives.
        setRichMediaClickHistory(prev => [...prev, { key: action.payload, value: action.text }]);

        await conv.sendMessage(action.payload);
        console.log('✅ Rich Media Action sent successfully:', action.payload);
      } else if (action.text) {
        await conv.sendMessage(action.text);
        console.log('✅ Rich Media Action text sent successfully:', action.text);
      }
    } catch (error) {
      console.error('❌ Failed to send rich media action:', error);
    }
  };

  // Function to test rich media detection on current messages
  const testRichMediaDetection = async () => {
    console.log('🧪 Testing Rich Media Detection...');
    if (messages.length > 0) {
      const richMediaMessages = await checkMessagesForRichMedia(messages);
      console.log(`✅ Rich Media Detection Test Complete: Found ${richMediaMessages.length} rich media messages`);
    } else {
      console.log('📝 No messages available for testing');
    }
  };

  // Function to analyze a specific message in detail
  const analyzeMessage = (messageIndex: number) => {
    if (messageIndex >= 0 && messageIndex < messages.length) {
      const message = messages[messageIndex];
      console.log(`🔬 Detailed Analysis of Message ${messageIndex}:`);
      console.log('📋 Basic Properties:', {
        index: message.index,
        sid: message.sid,
        body: message.body,
        author: message.author,
        dateCreated: message.dateCreated,
        participantSid: message.participantSid
      });
      console.log('🎨 Rich Media Properties:', {
        attachedMedia: message.attachedMedia,
        attributes: (message as any).attributes,
        type: (message as any).type,
        media: (message as any).media,
        contentSid: (message as any).contentSid,
        subject: (message as any).subject,
        _richMediaMetadata: (message as any)._richMediaMetadata
      });
      console.log('🔧 Full Message Object:', message);
    } else {
      console.log(`❌ Invalid message index: ${messageIndex}. Available range: 0-${messages.length - 1}`);
    }
  };

  // Function to manually test metadata retrieval for a specific message
  const testMessageMetadata = async (messageIndex: number) => {
    if (messageIndex >= 0 && messageIndex < messages.length) {
      const message = messages[messageIndex];
      console.log(`🧪 Testing metadata for message ${messageIndex}...`);

      if (typeof message.getChannelMetadata === 'function') {
        try {
          const metadata = await message.getChannelMetadata();
          if (metadata) {
            console.log('✅ Metadata retrieved:', metadata);

            // Check for actions
            if (metadata.type === 'dialogflowcx' && (metadata.data as any)?.queryResult?.responseMessages) {
              const responseMessages = (metadata.data as any).queryResult.responseMessages;
              console.log(`📦 Found ${responseMessages.length} response messages`);

              responseMessages.forEach((response: any, idx: number) => {
                if (response.payload?.payload?.actions) {
                  console.log(`🔘 Response ${idx} actions:`, response.payload.payload.actions);
                }
              });
            }
          } else {
            console.log('❌ No metadata returned');
          }
        } catch (error) {
          console.log('❌ Error getting metadata:', error);
        }
      } else {
        console.log('❌ getChannelMetadata method not available');
      }
    } else {
      console.log(`❌ Invalid message index: ${messageIndex}. Available range: 0-${messages.length - 1}`);
    }
  };

  // Rich media detection is handled in MessageList.tsx

  // Make the test functions available globally for browser console testing
  useEffect(() => {
    if (typeof window !== 'undefined') {
      (window as any).testRichMediaDetection = testRichMediaDetection;
      (window as any).analyzeMessage = analyzeMessage;
      (window as any).testMessageMetadata = testMessageMetadata;
      (window as any).currentMessages = messages;
      console.log(`🛠️ Rich Media Testing Functions Available:
        - testRichMediaDetection() - Test all messages
        - analyzeMessage(index) - Analyze specific message (0-${messages.length - 1})
        - testMessageMetadata(index) - Test metadata retrieval for specific message
        - currentMessages - Array of current messages`);
    }
  }, [messages]);



   
useEffect(() => {
  const url = window.location.href;
  const isDafaPlus = url.toLowerCase().includes("dafaplus");
  const isSpinmile = url.toLowerCase().includes("spinmile");

  setdafaplusBrand(isDafaPlus);
  setspinmileBrand(isSpinmile);

  console.log("Word found in URL:", isDafaPlus);
}, []);

  // DafaPlus only: keep a live localStorage mirror of this conversation's messages so
  // every tab open on the same chat shows the same lines as they arrive.
  const latestMessagesRef = useRef<Message[]>(messages);
  useEffect(() => {
    latestMessagesRef.current = messages;
  }, [messages]);

  // DafaPlus only: on mount (e.g. the user refreshed the chat page), immediately show
  // whatever chat lines were cached in localStorage instead of a blank chat while the
  // live Twilio fetch is still in flight. The live fetch that follows (see the token/
  // conversationSid init effect above) overwrites this with the authoritative history.
  useEffect(() => {
    if (!dafaplusBrand || !conversationSid || typeof window === 'undefined') {
      return;
    }

    if (latestMessagesRef.current.length > 0) {
      return;
    }

    try {
      const stored = localStorage.getItem(getDafaplusMessagesStorageKey(conversationSid));
      if (!stored) {
        return;
      }

      const cachedMessages = (JSON.parse(stored) as any[]).map(deserializeStoredMessage);
      if (cachedMessages.length === 0 || latestMessagesRef.current.length > 0) {
        return;
      }

      dispatch({ type: ACTION_SET_MESSAGES, payload: { messages: cachedMessages } });
    } catch (e) {
      console.error('Failed to hydrate DafaPlus chat messages from localStorage:', e);
    }
  }, [dafaplusBrand, conversationSid, dispatch]);

  // Attachment temporary URLs are signed per-request and only resolvable through the
  // live Twilio SDK media object, so we resolve them once here (where the SDK objects
  // still exist) and cache them - a plain object rebuilt in another tab has no way to
  // fetch its own URL.
  const dafaplusMediaUrlCacheRef = useRef<Record<string, string>>({});

  const serializeDafaplusMessageForStorage = useCallback(async (message: Message) => {
    const attachedMediaList = (((message as any).attachedMedia || []) as any[]);

    const attachedMedia = await Promise.all(attachedMediaList.map(async (media: any, mediaIndex: number) => {
      const mediaSid = media?.sid ?? media?.state?.sid ?? `${mediaIndex}`;
      const cacheKey = `${message.sid}:${mediaSid}`;
      let contentUrl = dafaplusMediaUrlCacheRef.current[cacheKey] || media?.contentUrl;

      if (!contentUrl) {
        try {
          if (typeof media?.getContentTemporaryUrl === 'function') {
            contentUrl = await media.getContentTemporaryUrl();
          } else if (typeof media?.getCachedTemporaryUrl === 'function') {
            contentUrl = await media.getCachedTemporaryUrl();
          }
        } catch (e) {
          console.error('Failed to resolve media temporary URL for cross-tab sync:', e);
        }
      }

      if (contentUrl) {
        dafaplusMediaUrlCacheRef.current[cacheKey] = contentUrl;
      }

      return {
        sid: media?.sid ?? media?.state?.sid,
        contentType: media?.contentType ?? media?.state?.contentType,
        filename: media?.filename ?? media?.state?.filename,
        size: media?.size ?? media?.state?.size,
        contentUrl,
      };
    }));

    return {
      index: message.index,
      sid: message.sid,
      author: message.author,
      body: message.body,
      dateCreated: message.dateCreated ? new Date(message.dateCreated as any).toISOString() : null,
      isSystemMessage: (message as any).isSystemMessage,
      type: (message as any).type,
      attachedMedia,
    };
  }, []);

  useEffect(() => {
    if (!dafaplusBrand || !conversationSid || typeof window === 'undefined') {
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const serialized = await Promise.all(messages.map(serializeDafaplusMessageForStorage));
        if (cancelled) {
          return;
        }
        localStorage.setItem(getDafaplusMessagesStorageKey(conversationSid), JSON.stringify(serialized));
      } catch (e) {
        console.error('Failed to persist DafaPlus chat messages for cross-tab sync:', e);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [messages, dafaplusBrand, conversationSid, serializeDafaplusMessageForStorage]);

  useEffect(() => {
    if (!dafaplusBrand || !conversationSid || typeof window === 'undefined') {
      return;
    }

    const storageKey = getDafaplusMessagesStorageKey(conversationSid);

    const handleStorage = (event: StorageEvent) => {
      if (event.key !== storageKey || !event.newValue) {
        return;
      }

      try {
        const storedMessages = (JSON.parse(event.newValue) as any[]).map(deserializeStoredMessage);
        const currentMessages = latestMessagesRef.current;
        const existingSids = new Set(currentMessages.map((m: any) => m.sid));
        const newMessages = storedMessages.filter((m) => m.sid && !existingSids.has(m.sid));

        if (newMessages.length === 0) {
          return;
        }

        const merged = [...currentMessages, ...newMessages].sort(
          (a: any, b: any) => (a.index ?? 0) - (b.index ?? 0)
        );

        dispatch({ type: ACTION_SET_MESSAGES, payload: { messages: merged } });
      } catch (e) {
        console.error('Failed to merge DafaPlus chat messages from another tab:', e);
      }
    };

    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [dafaplusBrand, conversationSid, dispatch]);

  if ((!token || !conversationSid)&& !dafaplusBrand) {
    if (userStatus === 'loading') {
      return (
        <Box sx={{ p: 2 }}>
          {statusBar('#FF875A', formData?.ChatLoader?.WaitingForAgentText || 'Connecting...')}
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        </Box>
      );
    }

    return (
      <Box sx={{ p: 2 }}>
        {statusBar('#5E5E5E', 'Missing Twilio session. Please start a chat from Pre-Chat.')}
      </Box>
    );
  }

  if (isInitializing && !dafaplusBrand) {
    return (
      <Box>
        {statusBar('#FF875A', formData?.ChatLoader?.WaitingForAgentText || 'Connecting...')}
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }


  const handleEmojiSelect = (emoji: any) => {
    if (inputRef.current) {
      const el = inputRef.current;
      const start = el.selectionStart || 0;
      const end = el.selectionEnd || 0;
      const updatedText = el.value.slice(0, start) + emoji.emoji + el.value.slice(end);
      setText(updatedText);

      // Update cursor position
      el.setSelectionRange(start + emoji.emoji.length, start + emoji.emoji.length);
      el.focus();
    } else {
      setText((prev) => prev + emoji.emoji);
    }
  };

const restartChat = async () => {
  try {
    hasVirtualAgent = false;
    inactivityCountRef.current = 0;
    // Leave the current conversation so the server knows the user is gone
    if (conversationRef.current) {
      try {
        await conversationRef.current.leave();
        console.log('👋 Successfully left the conversation on restart');
      } catch (err) {
        console.error('⚠️ Failed to leave conversation on restart:', err);
      }
    }
    // Reset the connection-loss restart flag and connectionState so the new session starts clean
    resetRestartButtonShown();
    dispatch({ type: ACTION_UPDATE_CONNECTION_STATE, payload: { connectionState: 'connected' } });
    // Set loading state and disable button
    setIsRestarting(true);
    resetInactivityTimer();

    // Reuse existing engagement parameters from Redux state
    const existingParams = engagementParams || {};

    // Create twilioFormData using existing parameters
    const twilioFormData = {
      friendlyName: existingParams.name || existingParams.username || 'Customer',
      email: existingParams.email || undefined,
      query: (formData?.ChatBox?.HelloMessageText ?? 'Hello'),
      ...existingParams, // Spread all existing engagement parameters
      Restart: 'true' // Mark this as a restart
    };

    console.log('🔄 Restarting chat with existing parameters:', twilioFormData);

    // Start the restart process
    dispatchTwilio(changeEngagementPhase({ phase: EngagementPhase.Loading }));

    const result = await dispatchTwilio(signInTwilio({ formData: twilioFormData }));

    if ((result as any).meta.requestStatus === 'fulfilled') {
      const { token, conversationSid } = (result as any).payload.userData || {};
      if (token && conversationSid) {
        dispatchTwilio(initSession({ token, conversationSid }) as any);
      }
      dispatch(setPage('chat'));
    } else {
      console.error('❌ Restart chat failed:', result);
      dispatchTwilio(changeEngagementPhase({ phase: EngagementPhase.PreEngagementForm }));
      // Reset restart button state if sign-in fails
      setIsRestarting(false);
    }
  } catch (error) {
    console.error('❌ Error during chat restart:', error);
    dispatchTwilio(changeEngagementPhase({ phase: EngagementPhase.PreEngagementForm }));
    // Reset restart button state if restart process fails
    setIsRestarting(false);
  }
};

function systemMessageCheck(lang: string): string {
    switch (lang) {
      case 'EN':
      case 'IN':
      case 'HI':
      case 'TE':
        return 'Please stay online while we connect you to our Online Support Representative.';
  
      case 'KR':
        return '저희 사이트를 방문해주셔서 감사합니다! 상담원과 연결중이오니 잠시만 기다려주시기 바랍니다.';
  
      case 'ID':
        return 'Mohon tunggu, kami sambungkan Anda pada salah satu agen representatif kami.';
  
      case 'TH':
        return 'ขออภัย ขณะนี้เจ้าหน้าที่ให้บริการเต็มทุกคู่สาย รบกวนรอสักครู่';
  
      case 'CN':
        return '目前客服人员繁忙，关于给您造成长时间的等待我们深表歉意。请您耐心等待，客服人员将立即为您服务。';
  
      default:
        return 'Please stay online while we connect you to our Online Support Representative.';
    }
  }

// Format current date and time for leave message
const now = new Date();
const leaveChatDateTime = now.toLocaleString('en-US', {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: true
});

  return (
    
    <Box sx={{
      ...containerStyles,
      backgroundColor: dafaplusBrand ? "#EFF4FF" : "#fff",
    }}>

    {!dafaplusBrand && (
      <>
        {isIndiaAccessingPkBd &&
          statusBar('#D32F2F', REGION_MISMATCH_MESSAGE)}

        {convState === "active" && isAgentConnected(participants, users) &&
          statusBar(
            "#1ED760",
            formData?.ChatLoader?.ConnectedToAgentText || "Connected to Agent"
          )}

        {convState === "active" && !isAgentConnected(participants, users) &&
          statusBar(
            "#FF875A",
            formData?.ChatLoader?.WaitingForAgentText || "Waiting for Agent..."
          )}

        {convState !== "active" &&
          statusBar(
            "#5E5E5E",
            formData?.ChatLoader?.SessionEndText || "Conversation ended"
          )}
      </>
    )}
  <Box sx={{ flex: 1, overflowY: 'auto', pb: dafaplusBrand ? "150px" : { xs: "80px", sm: "60px" }}}>
    <MessageList
      messages={messages}
      participants={participants}
      onScroll={handleScroll}
      listRef={listRef}
      myIdentity={(conversation as any)?.client?.user?.identity}
      onRichMediaAction={handleRichMediaAction}
      conversation={conversation}
      disabledRichMediaSids={disabledRichMediaSids}
      richMediaClickHistory={richMediaClickHistory}
    />
  </Box>
   
      {convState === "active" && dafaplusBrand && (
        <Box sx={{ ...inputBarStyles, padding: "10px 12px 12px" }}>
          <Box
            sx={{
              width: "100%",
              backgroundColor: "#fff",
              borderRadius: "16px",
              border: "1px solid #E3E7F0",
              boxShadow: "0 10px 24px rgba(17, 24, 39, 0.08)",
                padding: "10px 12px 3px",
            }}
          >
            <TextField
              inputRef={inputRef}
              fullWidth
              multiline
              minRows={2}
              maxRows={5}
              placeholder="Message"
              inputProps={{
                style: {
                  height: 110,
                },
              }}
              value={text}
              onChange={(e) => {
                setText(e.target.value);
                throttledTyping();
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  if (
                    (conversationRef.current || token) &&
                    (text.trim() || attachedFiles.length > 0) &&
                    !isSending
                  ) {
                    send();
                    inputRef.current?.focus();
                  }
                }
              }}
              disabled={isSending || !isAgentConnected(participants, users) || (!conversationRef.current && !token)}
              variant="standard"
              InputProps={{
                disableUnderline: true,
                startAdornment: (
                  <InputAdornment
                    position="start"
                    sx={{
                      position: "absolute",
                      left: 0,
                      bottom: "4px",
                      margin: 0,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: "2px" }}>
                      {formData?.ChatBox?.EnableAttachment && (
                        <AttachFileButton
                          ref={attachFileRef}
                          buttonSx={{
                            position: "static",
                            right: "auto",
                            top: "auto",
                            color: "#5F6368",
                            padding: "6px",
                          }}
                        />
                      )}
                      <IconButton
                        sx={{ color: "#5F6368", padding: "6px" }}
                        aria-label="emoji"
                        onClick={() => setIsEmojiShown((prev) => !prev)}
                      >
                        <EmojiEmotionsIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment
                    position="end"
                    sx={{
                      position: "absolute",
                      right: 0,
                      bottom: "3px",
                      margin: 0,
                    }}
                  >
                    <IconButton
                      sx={{
                        width: "36px",
                        height: "36px",
                        backgroundColor: "#930102",
                        color: "#fff",
                        "&:hover": {
                          backgroundColor: "#7d0102",
                        },
                        "&.Mui-disabled": {
                          backgroundColor: "#b56c6d",
                          color: "#fff",
                        },
                      }}
                      aria-label="send"
                      onClick={send}
                      disabled={
                        isSending ||
                        (!text.trim() && attachedFiles.length === 0) ||
                        (!conversationRef.current && !token)
                      }
                    >
                      <KeyboardControlKeyIcon sx={{ color: "#fff" }} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                width: "100%",
                "& .MuiInput-root": {
                  position: "relative",
                  display: "block",
                  width: "100%",
                  backgroundColor: "#fff",
                  padding: "18px 0 0",
                },
                "& .MuiInputBase-adornedStart": {
                  paddingLeft: "0",
                },
                "& .MuiInputBase-adornedEnd": {
                  paddingRight: "0",
                },
                "& .MuiInputAdornment-root": {
                  maxHeight: "none",
                },
                "& .MuiInputLabel-root": {
                  color: "#6B7280",
                },
                "& .MuiInputLabel-root.Mui-focused": {
                  color: "#6B7280",
                },
                "& .MuiInputLabel-root.MuiFormLabel-root": {
                  left: "0",
                },
                "& .MuiInput-root.MuiInputBase-multiline": {
                  paddingTop: "0",
                  marginTop: "0",
                },
                "& .MuiOutlinedInput-input": {
                  boxSizing: "border-box",
                  minHeight: "52px",
                  overflowY: "auto",
                  padding: "0",
                  width: "100%",
                },
                "& .MuiInput-input": {
                  width: "100%",
                },
                "& .MuiInputBase-inputMultiline": {
                  width: "100%",
                  minHeight: "110px",
                  maxHeight: "110px",
                  paddingBottom: "34px",
                  boxSizing: "border-box",
                },
              }}
            />
          </Box>

          {isEmojiShown && (
            <Box
              sx={{
                position: "absolute",
                bottom: "110px",
                left: "12px",
                zIndex: 2,
              }}
            >
              <EmojiPicker onEmojiClick={handleEmojiSelect} />
            </Box>
          )}
        </Box>
      )}

      {convState === "active" && !dafaplusBrand && (
        <Box sx={inputBarStyles}>
          <IconButton
            sx={{
              ...emojiButtonStyles,
              top: "18px",
              left: "19px",
            }}
            aria-label="emoji"
            onClick={() => setIsEmojiShown((prev) => !prev)}
          >
            <EmojiEmotionsIcon />
          </IconButton>
          {isEmojiShown && (
            <Box
              sx={{
                position: "absolute",
                bottom: "60px",
                left: "10px",
                zIndex: 2,
              }}
            >
              <EmojiPicker onEmojiClick={handleEmojiSelect} />
            </Box>
          )}

          <Box>
            {formData?.ChatBox?.EnableAttachment && (
              <AttachFileButton ref={attachFileRef} />
            )}
          </Box>

          <TextField
            inputRef={inputRef}
            fullWidth
            placeholder={
              formData?.ChatBox?.TypeMessagePlaceholder || "Type your message"
            }
            value={text}
            onChange={(e) => {
              setText(e.target.value);
              throttledTyping();
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                if (
                  (conversationRef.current || token) &&
                  (text.trim() || attachedFiles.length > 0) &&
                  !isSending
                ) {
                  send();
                  inputRef.current?.focus();
                }
              }
            }}
            disabled={isSending || (!spinmileBrand && !isAgentConnected(participants, users)) || (!conversationRef.current && !token)}
            sx={{
              height: "auto",
              borderRadius: "50px",
              border: "none",
              "& .MuiOutlinedInput-root": {
                borderRadius: "50px",
                padding: "0 20px 0 30px",
                borderColor: "rgba(0, 0, 0, 0.87)",
              },
              "& .MuiOutlinedInput-input": {
                width: "calc(100% - 90px)",
                height: "auto",
                boxSizing: "border-box",
              },
              backgroundColor: "#E5E5EB",
              "& .Mui-focused": {
                backgroundColor: "#fff",
              },
            }}
          />
          <IconButton
            sx={sendButtonStyle}
            aria-label="send"
            onClick={send}
            disabled={
              isSending ||
              (!text.trim() && attachedFiles.length === 0) ||
              (!conversationRef.current && !token)
            }
          >
            <SendIcon />
          </IconButton>
        </Box>
      )}

      {attachedFiles.length ? (
        <Box sx={previewBarStyles}>
          {attachedFiles.map((f: File, i: number) => (
            <FilePreview key={i} file={f} disabled={isSending} />
          ))}
        </Box>
      ) : null}

      {convState !== "active" && (
        <Box sx={{ textAlign: "center", p: 2 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              my: 2,
              justifyContent: "center",
            }}
          >
            <Typography
              variant="caption"
              sx={{ color: "#888", mx: 1, textAlign: "center" }}
            >
              {getAgentName(participants, users)} {formData?.ChatBox?.LeaveChatMessage || 'has left the chatgroup'} - ({leaveChatDateTime})
            </Typography>
          </Box>
          <Button
            variant="contained"
            loading={isRestarting}
            disabled={isRestarting}
            onClick={restartChat}
          >
            {isRestarting
              ? "Restarting..."
              : (formData?.ReconnectChat?.StartNewSessionLabel || "Start new session")
            }
          </Button>
        </Box>
      )}
    </Box>
  );
}

